import { DateInput } from '../../../../components/DateInput/DateInput';
import React, { useState, useEffect, useRef } from 'react';
import { profileService, UserNominationDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validateNominationDetails, localTodayIso, ageInYears } from '../../../../utils/investorValidation';
import { isShareFilled, updateNomineeShare1, updateNomineeShare2 } from '../../../../utils/formHelpers';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import {
  NOMINATION_RELATIONSHIP_OPTIONS,
  NOMINATION_DOC_TYPE_OPTIONS_NRI_OCI,
  NOMINATION_DOC_TYPE_OPTIONS_RI,
} from '../shared/constants';
import { isResidentIndividual } from '../../../../config/profileVisibility';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { agentReadOnlyNote } from '../../../../utils/delegationScope';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { PremiumSelect } from '../../../../components/PremiumSelect/PremiumSelect';
import { contentService, MasterStateDto } from '../../../../services/content.service';

/** Laravel repeats one nominee markup block three times. */
const NOMINEE_INDEXES: number[] = [1, 2, 3];

/** Joins the name parts we hold, dropping blanks so no double spaces appear. */
const joinNameParts = (...parts: (string | undefined | null)[]): string =>
  parts.map((p) => (p ?? '').trim()).filter(Boolean).join(' ');

/**
 * The nominee's name as it can be derived from Personal Information, for the three
 * relationships whose name the investor has already given us. Title (MR/MRS) is left off
 * deliberately — this field holds a person's name, not a salutation.
 *
 * Brother/Sister/Son/Daughter have no source field, so they return '' and stay manual.
 * Not a Laravel behaviour (its only nominee-name script is a dev console helper) — this is
 * a product addition requested 2026-09-10.
 */
const nomineeNameFromRelation = (
  relation: string,
  personalInfo: SharedFormContext['personalInfo']
): string => {
  if (!personalInfo) return '';
  switch (relation) {
    case 'Father':
      return joinNameParts(
        personalInfo.fathersFirstName, personalInfo.fathersMiddleName, personalInfo.fathersLastName);
    case 'Mother':
      return joinNameParts(
        personalInfo.motherFirstName, personalInfo.motherMiddleName, personalInfo.motherLastName);
    case 'Spouse':
      return joinNameParts(
        personalInfo.spouseName, personalInfo.spouseMiddleName, personalInfo.spouseLastName);
    default:
      return '';
  }
};

/** Parity fix (#4): a nominee is a minor when their DOB is under 18 years ago — guardian block
 *  auto-shows. Uses the shared age helper so the form and the validator can never disagree. */
const isMinorFromDob = (dob?: string): boolean => {
  if (!dob) return false;
  const age = ageInYears(dob);
  return !isNaN(age) && age < 18;
};

interface NominationFormProps {
  initialData: UserNominationDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const NominationForm: React.FC<NominationFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const [formData, setFormData] = useState<Partial<UserNominationDto>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Doc-type list is investor-type dependent (user decision 2026-09-04):
  // NRI/OCI = Laravel's PAN/Passport pair; RI = Aadhaar/Other.
  const isRi = isResidentIndividual(sharedContext.dashboardData?.investor?.investorType ?? undefined);
  const docTypeOptions = isRi ? NOMINATION_DOC_TYPE_OPTIONS_RI : NOMINATION_DOC_TYPE_OPTIONS_NRI_OCI;

  useEffect(() => {
    if (initialData) {
      // Legacy-value normalisation: the old list stored 'PASSPORT' (uppercase) where
      // Laravel (and the new list) use 'Passport' — remap so existing rows still show
      // their selection. Values no longer offered (e.g. 'DRIVING LICENSE') are left
      // as-is; the dropdown renders unselected and validation asks for a re-pick.
      const hydrated: any = { ...initialData };
      (['nomineeDocType1', 'nomineeDocType2', 'nomineeDocType3',
        'guardianDocType1', 'guardianDocType2', 'guardianDocType3'] as const).forEach((k) => {
        if (String(hydrated[k] ?? '').toUpperCase() === 'PASSPORT') hydrated[k] = 'Passport';
      });
      // Laravel defaults nominee 1's share to 100 (blade `?? 100`, plus its JS re-applies it
      // when the field loads empty or 0). A saved value always wins — this only fills the gap
      // for a row that was created without one.
      if (!isShareFilled(hydrated.nomineeShare1)) hydrated.nomineeShare1 = 100;
      setFormData(hydrated);
    } else {
      // Fresh journey, nothing saved yet — same default.
      setFormData((prev) => (isShareFilled(prev.nomineeShare1) ? prev : { ...prev, nomineeShare1: 100 }));
    }
  }, [initialData]);

  const showNominee2Section = isShareFilled(formData.nomineeShare1) && (formData.nomineeShare1 ?? 0) < 100;
  const showNominee3Section = isShareFilled(formData.nomineeShare2) &&
    (formData.nomineeShare1 ?? 0) + (formData.nomineeShare2 ?? 0) < 100;

  const updateNomineeShare1FromInput = (val: string) => {
    updateNomineeShare1(val, formData, setFormData);
  };

  const updateNomineeShare2FromInput = (val: string) => {
    updateNomineeShare2(val, formData, setFormData);
  };

  // Corrected fields drop their error immediately (remove-only live re-check).
  useLiveErrorClearing(errors, setErrors, () => validateNominationDetails(formData as UserNominationDto), [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateNominationDetails(formData as UserNominationDto);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      await profileService.updateNomination(formData as UserNominationDto);
      toast.success('Nomination details updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };
  // ---- Shared accessors for the three identical nominee blocks ----------
  // Laravel renders nominee 1..3 from one repeated markup block, so the fields
  // are addressed by a numeric suffix rather than hand-written three times.
  const str = (key: string): string => {
    const v = (formData as Record<string, any>)[key];
    return v === undefined || v === null ? '' : String(v);
  };
  const set = (key: string, value: any) => setFormData({ ...formData, [key]: value });
  const err = (key: string): string | undefined => errors[key];

  const setShare = (n: number, raw: string) => {
    if (n === 1) return updateNomineeShare1FromInput(raw);
    if (n === 2) return updateNomineeShare2FromInput(raw);
    set('nomineeShare3', raw ? Number.parseInt(raw, 10) : undefined);
  };

  /**
   * Names this form auto-filled, per nominee. A relationship change may replace a name we
   * put there, but never one the investor typed — that is the whole point of tracking it.
   */
  const autoFilledNameRef = useRef<Record<number, string>>({});

  const setRelation = (n: number, val: string) => {
    const suggested = nomineeNameFromRelation(val, sharedContext.personalInfo);
    const current = String((formData as Record<string, any>)[`nomineeName${n}`] ?? '').trim();
    const ours = current !== '' && current === autoFilledNameRef.current[n];

    // Decide BEFORE touching state, and keep the ref write out of the updater. React invokes
    // state updaters TWICE under StrictMode: the first pass cleared the ref, so the second saw
    // ours=false and silently kept the old name — switching Father -> Spouse left "RAM BHOI"
    // standing under Spouse (caught by the E2E run 2026-09-12).
    let nextName: string | null = null;                       // null = leave the name alone
    if (suggested && (current === '' || ours)) nextName = suggested;
    else if (!suggested && ours) nextName = '';               // no name for this relationship

    if (nextName === null) {
      set(`nomineeRelation${n}`, val);
      return;
    }
    if (nextName) autoFilledNameRef.current[n] = nextName;
    else delete autoFilledNameRef.current[n];
    setFormData((prev) => ({
      ...(prev as Record<string, any>),
      [`nomineeRelation${n}`]: val,
      [`nomineeName${n}`]: nextName,
    }) as Partial<UserNominationDto>);
  };

  const countryOptions = React.useMemo(
    () =>
      sharedContext.taxResidencyCountries
        .filter((c) => c.ssName)
        .map((c) => ({ value: c.ssName!, label: c.ssName!.toUpperCase() })),
    [sharedContext.taxResidencyCountries]
  );

  const isdOptions = React.useMemo(
    () =>
      sharedContext.isdCodes.map((c) => ({
        value: (c.id ?? c.myRowId)?.toString() ?? '',
        label: `${c.countryName ?? c.nationality ?? '—'} (${c.codeValue != null ? `+${c.codeValue}` : ''})`,
      })),
    [sharedContext.isdCodes]
  );

  // Per-nominee state list, reloaded from master_states when that nominee's
  // country changes (Laravel's dependent State dropdown).
  const [stateOptions, setStateOptions] = useState<Record<number, { value: string; label: string }[]>>({});
  const nomineeCountries = NOMINEE_INDEXES.map((n) => str(`nomineeCountry${n}`)).join('|');

  useEffect(() => {
    let cancelled = false;
    const countries = nomineeCountries.split('|');

    Promise.all(
      countries.map((country) =>
        country
          ? contentService.getStates(country).catch(() => [] as MasterStateDto[])
          : Promise.resolve([] as MasterStateDto[])
      )
    ).then((lists) => {
      if (cancelled) return;
      const next: Record<number, { value: string; label: string }[]> = {};
      NOMINEE_INDEXES.forEach((n, i) => {
        next[n] = (lists[i] ?? [])
          .filter((s) => s.ssName)
          .map((s) => ({ value: s.ssName!, label: s.ssName!.toUpperCase() }));
      });
      setStateOptions(next);
    });

    return () => { cancelled = true; };
  }, [nomineeCountries]);


  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* <h3>Nomination Details</h3> */}
      <div className="form-group form-group--full" style={{ marginBottom: '1rem' }}>
        <span className="d-block mb-1">Do you want to appoint a Nominee? <span className="text-danger">*</span></span>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <label className="mb-0">
            <input
              type="radio"
              name="appointNominee"
              checked={formData.appointNominee === true}
              onChange={() => setFormData({ ...formData, appointNominee: true })}
            />{' '}
            Yes
          </label>
          <label className="mb-0">
            <input
              type="radio"
              name="appointNominee"
              checked={formData.appointNominee === false}
              onChange={() => setFormData({ ...formData, appointNominee: false })}
            />{' '}
            No
          </label>
        </div>
        {err('appointNominee') && (
          <div className="text-danger small mt-1" role="alert">{err('appointNominee')}</div>
        )}
      </div>
      {errors.totalShare && (
        <div className="alert alert-danger" role="alert" style={{ marginBottom: '1rem' }}>
          {errors.totalShare}
        </div>
      )}
      {formData.appointNominee === true && (
        <div className="investor-profile__grid">
          {NOMINEE_INDEXES.map((n) => {
            if (n === 2 && !showNominee2Section) return null;
            if (n === 3 && !showNominee3Section) return null;

            const isMinor = isMinorFromDob(str(`nomineeDob${n}`));

            return (
              <React.Fragment key={n}>
                <div className="form-group form-group--full">
                  <h4 style={{ margin: '0.25rem 0 0.5rem', color: '#333', borderBottom: '1px solid #eee', paddingBottom: '0.35rem' }}>
                    Nominee {n}
                  </h4>
                </div>

                {/* Laravel field order: relationship / name / share, then the full
                    address block, then the nominee's contact + document details. */}
                <div className="form-group">
                  <label>Relationship with Applicant <span className="text-danger">*</span></label>
                  <PremiumSelect
                    value={str(`nomineeRelation${n}`)}
                    onChange={(val) => setRelation(n, val)}
                    options={NOMINATION_RELATIONSHIP_OPTIONS}
                    error={err(`nomineeRelation${n}`)}
                  />
                </div>

                <div className="form-group">
                  <label>Name of the Nominee <span className="text-danger">*</span></label>
                  <input
                    value={str(`nomineeName${n}`)}
                    onChange={(e) => set(`nomineeName${n}`, e.target.value)}
                    className={err(`nomineeName${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeName${n}`) && <div className="invalid-feedback">{err(`nomineeName${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Nominee Share (%) <span className="text-danger">*</span></label>
                  {/* Nominee 3's share is always the remainder of 100, so it is derived rather
                      than typed (Laravel marks it readonly too). Leaving it editable let a user
                      enter a total that only failed at submit. */}
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={str(`nomineeShare${n}`)}
                    onChange={(e) => setShare(n, e.target.value)}
                    readOnly={n === 3}
                    title={n === 3 ? 'Calculated automatically as the remaining share' : undefined}
                    className={err(`nomineeShare${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeShare${n}`) && <div className="invalid-feedback">{err(`nomineeShare${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Date of Birth of Nominee <span className="text-danger">*</span></label>
                  <DateInput
                    value={str(`nomineeDob${n}`)}
                    onChange={(value) => set(`nomineeDob${n}`, value)}
                    max={localTodayIso()}
                    className={err(`nomineeDob${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeDob${n}`) && <div className="invalid-feedback d-block">{err(`nomineeDob${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Address <span className="text-danger">*</span></label>
                  <input
                    value={str(`nomineeAddress${n}`)}
                    onChange={(e) => set(`nomineeAddress${n}`, e.target.value)}
                    className={err(`nomineeAddress${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeAddress${n}`) && <div className="invalid-feedback">{err(`nomineeAddress${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Country <span className="text-danger">*</span></label>
                  <PremiumSelect
                    value={str(`nomineeCountry${n}`)}
                    onChange={(val) => {
                      // Changing country invalidates the state chosen under it.
                      setFormData({ ...formData, [`nomineeCountry${n}`]: val, [`nomineeState${n}`]: '' });
                    }}
                    options={countryOptions}
                    placeholder="Select Country"
                    error={err(`nomineeCountry${n}`)}
                    searchable
                  />
                </div>

                <div className="form-group">
                  <label>State <span className="text-danger">*</span></label>
                  <PremiumSelect
                    value={str(`nomineeState${n}`)}
                    onChange={(val) => set(`nomineeState${n}`, val)}
                    options={stateOptions[n] ?? []}
                    placeholder="Select State"
                    error={err(`nomineeState${n}`)}
                    searchable
                  />
                </div>

                <div className="form-group">
                  <label>City <span className="text-danger">*</span></label>
                  <input
                    value={str(`nomineeCity${n}`)}
                    onChange={(e) => set(`nomineeCity${n}`, e.target.value)}
                    className={err(`nomineeCity${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeCity${n}`) && <div className="invalid-feedback">{err(`nomineeCity${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Pincode <span className="text-danger">*</span></label>
                  <input
                    value={str(`nomineePincode${n}`)}
                    onChange={(e) => set(`nomineePincode${n}`, e.target.value)}
                    className={err(`nomineePincode${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineePincode${n}`) && <div className="invalid-feedback">{err(`nomineePincode${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Email Address of Nominee <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    value={str(`nomineeEmail${n}`)}
                    onChange={(e) => set(`nomineeEmail${n}`, e.target.value)}
                    className={err(`nomineeEmail${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeEmail${n}`) && <div className="invalid-feedback">{err(`nomineeEmail${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>ISD Code <span className="text-danger">*</span></label>
                  <PremiumSelect
                    value={str(`nomineeCountrycode${n}`)}
                    onChange={(val) => set(`nomineeCountrycode${n}`, val ? Number(val) : undefined)}
                    options={isdOptions}
                    placeholder="Select ISD"
                    error={err(`nomineeCountrycode${n}`)}
                    searchable
                  />
                </div>

                <div className="form-group">
                  <label>Mobile Number of Nominee <span className="text-danger">*</span></label>
                  <input
                    type="tel"
                    value={str(`nomineeMobile${n}`)}
                    onChange={(e) => set(`nomineeMobile${n}`, e.target.value)}
                    className={err(`nomineeMobile${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeMobile${n}`) && <div className="invalid-feedback">{err(`nomineeMobile${n}`)}</div>}
                </div>

                <div className="form-group">
                  <label>Nominee Document Type <span className="text-danger">*</span></label>
                  <PremiumSelect
                    value={str(`nomineeDocType${n}`)}
                    onChange={(val) => set(`nomineeDocType${n}`, val)}
                    options={docTypeOptions}
                    error={err(`nomineeDocType${n}`)}
                  />
                </div>

                <div className="form-group">
                  <label>Document Number <span className="text-danger">*</span></label>
                  <input
                    value={str(`nomineeDocNo${n}`)}
                    onChange={(e) => set(`nomineeDocNo${n}`, e.target.value)}
                    className={err(`nomineeDocNo${n}`) ? 'form-control is-invalid' : 'form-control'}
                  />
                  {err(`nomineeDocNo${n}`) && <div className="invalid-feedback">{err(`nomineeDocNo${n}`)}</div>}
                </div>

                {/* Guardian block — Laravel shows it only when the nominee is a
                    minor, derived from that nominee's date of birth. */}
                {isMinor && (
                  <>
                    <div className="form-group">
                      <label>Name of Guardian <span className="text-danger">*</span></label>
                      <input
                        value={str(`guardianName${n}`)}
                        onChange={(e) => set(`guardianName${n}`, e.target.value)}
                        className={err(`guardianName${n}`) ? 'form-control is-invalid' : 'form-control'}
                      />
                      {err(`guardianName${n}`) && <div className="invalid-feedback">{err(`guardianName${n}`)}</div>}
                    </div>

                    <div className="form-group">
                      <label>Guardian Identification Document Type <span className="text-danger">*</span></label>
                      <PremiumSelect
                        value={str(`guardianDocType${n}`)}
                        onChange={(val) => set(`guardianDocType${n}`, val)}
                        options={docTypeOptions}
                        error={err(`guardianDocType${n}`)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Document Number <span className="text-danger">*</span></label>
                      <input
                        value={str(`guardianDocNo${n}`)}
                        onChange={(e) => set(`guardianDocNo${n}`, e.target.value)}
                        className={err(`guardianDocNo${n}`) ? 'form-control is-invalid' : 'form-control'}
                      />
                      {err(`guardianDocNo${n}`) && <div className="invalid-feedback">{err(`guardianDocNo${n}`)}</div>}
                    </div>

                    <div className="form-group">
                      <label>Country Code <span className="text-danger">*</span></label>
                      <PremiumSelect
                        value={str(`guardianCountrycode${n}`)}
                        onChange={(val) => set(`guardianCountrycode${n}`, val ? Number(val) : undefined)}
                        options={isdOptions}
                        placeholder="Select ISD"
                        error={err(`guardianCountrycode${n}`)}
                        searchable
                      />
                    </div>

                    <div className="form-group">
                      <label>Mobile Number of Guardian with ISD Code <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        value={str(`guardianMobile${n}`)}
                        onChange={(e) => set(`guardianMobile${n}`, e.target.value)}
                        className={err(`guardianMobile${n}`) ? 'form-control is-invalid' : 'form-control'}
                      />
                      {err(`guardianMobile${n}`) && <div className="invalid-feedback">{err(`guardianMobile${n}`)}</div>}
                    </div>

                    <div className="form-group">
                      <label>Email Address of Guardian <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        value={str(`guardianEmail${n}`)}
                        onChange={(e) => set(`guardianEmail${n}`, e.target.value)}
                        className={err(`guardianEmail${n}`) ? 'form-control is-invalid' : 'form-control'}
                      />
                      {err(`guardianEmail${n}`) && <div className="invalid-feedback">{err(`guardianEmail${n}`)}</div>}
                    </div>

                    <div className="form-group">
                      <label>PAN number of Guardian <span className="text-danger">*</span></label>
                      <input
                        value={str(`guardianPanNo${n}`)}
                        onChange={(e) => set(`guardianPanNo${n}`, e.target.value)}
                        placeholder="ABCDE1234F"
                        maxLength={10}
                        className={err(`guardianPanNo${n}`) ? 'form-control is-invalid' : 'form-control'}
                      />
                      {err(`guardianPanNo${n}`) && <div className="invalid-feedback">{err(`guardianPanNo${n}`)}</div>}
                    </div>

                    <div className="form-group">
                      <label>Date of Birth of Guardian <span className="text-danger">*</span></label>
                      <DateInput
                        value={str(`guardianDob${n}`)}
                        onChange={(value) => set(`guardianDob${n}`, value)}
                        max={localTodayIso()}
                        className={err(`guardianDob${n}`) ? 'form-control is-invalid' : 'form-control'}
                      />
                      {err(`guardianDob${n}`) && <div className="invalid-feedback d-block">{err(`guardianDob${n}`)}</div>}
                    </div>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && agentReadOnlyNote(delegationPerms, 'ONBOARDING') && (
        <small className="text-warning d-block mt-2">
          {agentReadOnlyNote(delegationPerms, 'ONBOARDING')}
        </small>
      )}
    </form>
  );
};
